import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { track } from '../lib/amplitude';
import { ApiError, errorMessage } from '../lib/api';
import { emptyReactionTotals } from '../lib/reactions';
import type { Reaction, ReactionKind, ReactionsResponse, ReactionTotals } from '../lib/types';
import { qk } from '../queries/keys';
import { fetchReactions, postReaction, ensureAnonId } from '../queries/share';

/**
 * Reactions for one share, with the optimistic write that makes them feel
 * instant.
 *
 * The point of a reaction is that it costs nothing: you tap it while still
 * watching. A round trip before anything appears would make it cost something,
 * so the reaction is written into the cache immediately and reconciled after.
 *
 * Rollback is deliberately *visible*. A reaction that silently vanishes is
 * worse than one that never appeared, because the viewer believes they left
 * it. So a failed write removes the optimistic row and says why, in words.
 */

/** Optimistic rows carry a recognisable id, so nothing needs a client-only field. */
const PENDING_PREFIX = 'pending:';

let pendingSeq = 0;

export function isPendingReactionId(id: string): boolean {
  return id.startsWith(PENDING_PREFIX);
}

interface PostInput {
  kind: ReactionKind;
  timecodeSec: number;
}

interface MutationContext {
  previous: ReactionsResponse | undefined;
  optimisticId: string;
}

export interface ReactionsState {
  reactions: Reaction[];
  totals: ReactionTotals;
  /** The first load, before anything can be drawn. */
  loading: boolean;
  /**
   * The share is gone, or this viewer is not allowed to see it. The whole
   * surface should disappear rather than offer a button that cannot work.
   */
  unavailable: boolean;
  /** A load failure that is not a 404. Reacting still works. */
  loadError: string | null;
  retry: () => void;
  /** Records a reaction at `timecodeSec`. Never throws. */
  react: (kind: ReactionKind, timecodeSec: number) => void;
  /** The kind currently in flight, for a pressed-looking button. */
  posting: ReactionKind | null;
  /** Set when a write was rolled back. Cleared on the next attempt. */
  postError: string | null;
  /** The last kind that landed, for a confirmation the viewer can read. */
  lastAccepted: ReactionKind | null;
}

export function useReactions(shareId: string, enabled: boolean): ReactionsState {
  const queryClient = useQueryClient();
  const [postError, setPostError] = useState<string | null>(null);
  const [lastAccepted, setLastAccepted] = useState<ReactionKind | null>(null);
  const acceptedTimer = useRef<number | null>(null);

  const query = useQuery({
    queryKey: qk.reactions(shareId),
    queryFn: () => fetchReactions(shareId),
    enabled,
    staleTime: 15_000,
    // A 404 is an answer, not a hiccup. Retrying it three times only delays
    // hiding a surface that is never going to work for this viewer.
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.isNotFound) && failureCount < 2,
  });

  const mutation = useMutation<Reaction, unknown, PostInput, MutationContext>({
    mutationFn: async (input) =>
      postReaction(shareId, {
        kind: input.kind,
        timecodeSec: input.timecodeSec,
        // Always sent. A signed-in viewer is identified by their token and the
        // API prefers it, but a token that failed to refresh would otherwise
        // turn a tap into a 400 rather than an anonymous reaction. The id is
        // the API's own, minted on first need.
        anonId: await ensureAnonId(),
      }),

    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: qk.reactions(shareId) });
      const previous = queryClient.getQueryData<ReactionsResponse>(qk.reactions(shareId));

      pendingSeq += 1;
      const optimisticId = `${PENDING_PREFIX}${pendingSeq}`;
      const optimistic: Reaction = {
        id: optimisticId,
        kind: input.kind,
        timecodeSec: input.timecodeSec,
        createdAt: new Date().toISOString(),
      };

      // Seeding an empty base matters: the first reaction on a recording whose
      // GET failed still has somewhere to land.
      const base: ReactionsResponse = previous ?? { reactions: [], totals: emptyReactionTotals() };
      queryClient.setQueryData<ReactionsResponse>(qk.reactions(shareId), {
        reactions: [...base.reactions, optimistic],
        totals: { ...base.totals, [input.kind]: base.totals[input.kind] + 1 },
      });

      return { previous, optimisticId };
    },

    onError: (error, _input, context) => {
      // Put the cache back exactly as it was. `undefined` is a real state here
      // (nothing had loaded), so it is restored as `undefined`, not as an
      // empty response that would hide a genuine load failure.
      if (context) queryClient.setQueryData(qk.reactions(shareId), context.previous);
      setPostError(
        error instanceof ApiError && error.isNotFound
          ? 'That reaction did not save. This recording is no longer accepting them.'
          : `That reaction did not save. ${errorMessage(error)}`,
      );
    },

    onSuccess: (created, _input, context) => {
      // Swap the placeholder for the server's row in place, so the marker does
      // not jump while the refetch is in flight.
      queryClient.setQueryData<ReactionsResponse>(qk.reactions(shareId), (current) => {
        if (!current) return current;
        return {
          ...current,
          reactions: current.reactions.map((reaction) =>
            context && reaction.id === context.optimisticId ? created : reaction,
          ),
        };
      });
      setLastAccepted(created.kind);
      track('Reaction Added', { video_id: shareId, kind: created.kind });
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.reactions(shareId) });
    },
  });

  // The "saved" confirmation is transient; the totals are the permanent record.
  useEffect(() => {
    if (lastAccepted === null) return;
    if (acceptedTimer.current !== null) window.clearTimeout(acceptedTimer.current);
    acceptedTimer.current = window.setTimeout(() => setLastAccepted(null), 2400);
    return () => {
      if (acceptedTimer.current !== null) window.clearTimeout(acceptedTimer.current);
    };
  }, [lastAccepted]);

  const { mutate } = mutation;

  const react = useCallback(
    (kind: ReactionKind, timecodeSec: number) => {
      setPostError(null);
      const safe = Number.isFinite(timecodeSec) && timecodeSec > 0 ? Math.floor(timecodeSec) : 0;
      mutate({ kind, timecodeSec: safe });
    },
    [mutate],
  );

  const notFound = query.error instanceof ApiError && query.error.isNotFound;
  const data = query.data;

  return {
    reactions: data?.reactions ?? [],
    totals: data?.totals ?? emptyReactionTotals(),
    loading: query.isLoading,
    unavailable: notFound,
    loadError: query.isError && !notFound ? errorMessage(query.error) : null,
    retry: () => void query.refetch(),
    react,
    posting: mutation.isPending ? (mutation.variables?.kind ?? null) : null,
    postError,
    lastAccepted,
  };
}
